import { NavLink, useLocation } from 'react-router-dom';
import { UsersRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePortalAuth } from '../portal/PortalAuthContext';
import { usePortalDestination } from '../portal/PortalDestinationContext';
import { CareProvider, useCare } from './CareContext';
import { CareRequestsView } from './CareRequestsView';
import { StaffRxReviewPanel } from './StaffRxReviewPanel';
import { StaffRxFulfilmentPanel } from './StaffRxFulfilmentPanel';
import { StaffRefillReviewPanel } from './StaffRefillReviewPanel';
import { StaffAdrReviewPanel } from './StaffAdrReviewPanel';
import { CareBillsView } from './CareBillsView';
import { CareVerificationView } from './CareVerificationView';
import { IdentityQueueView } from './IdentityQueueView';
import { CareChatView } from './CareChatView';
import { CareCustomersView, CareTeamView } from './CareOtherViews';
import { CareTriageView } from './CareTriageView';
import { CareHandoverView } from './CareHandoverView';
import { CustomerAdminView } from './CustomerAdminView';
import { PortalOTCStaff } from '../components/PortalOTCStaff';
import PortalClinicalSharePage from '../portal/pages/PortalClinicalSharePage';
import { PortalRxTabs } from '../portal/PortalRxTabs';
import './care.css';

const sections = [['records', 'Clients'], ['orders', 'Online orders'], ['prescriptions', 'Prescriptions'], ['fulfilment', 'Rx Fulfilment'], ['refills', 'Refills'], ['reactions', 'Reaction Reports'], ['conversations', 'Conversations'], ['handover', 'Handover'], ['verification', 'ID verification'], ['team', 'Response team'], ['admin', 'Client admin']];

const branchLabel = (user, branch) => {
  if (branch === user?.selected_tenant_id && user?.selected_tenant_name) return user.selected_tenant_name;
  if (branch === user?.tenant_id && user?.tenant_name) return user.tenant_name;
  try {
    const saved = JSON.parse(localStorage.getItem('pharmxai_active_branch') || 'null');
    if (saved?.tenant_id === branch && saved.name) return saved.name;
  } catch { /* ignore bad saved value */ }
  return 'Selected branch';
};

const StaffOrdersSection = () => (
  <div className="space-y-8" data-testid="customer-orders-section">
    <a href="#staff-otc-anchor" className="text-sm underline" data-testid="customer-orders-jump-link">Jump to collection orders ↓</a>
    <CareRequestsView exclude={['otc']} />
    <div id="staff-otc-anchor" className="border-t pt-6">
      <PortalOTCStaff />
    </div>
  </div>
);

export const CareWorkspace = ({ section, patientMode = false, href }) => {
  const { branch, user } = useCare();
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const visibleSections = sections.filter(([key]) => key !== 'admin' || isAdmin);
  const content = { records: <CareCustomersView />, admin: isAdmin ? <CustomerAdminView /> : <CareCustomersView />, triage: <CareTriageView />, handover: <CareHandoverView />, conversations: <CareChatView />, chat: <CareChatView />, orders: patientMode ? <CareRequestsView /> : <StaffOrdersSection />, prescriptions: patientMode ? <CareRequestsView only="prescription" /> : <StaffRxReviewPanel />, fulfilment: <StaffRxFulfilmentPanel />, refills: patientMode ? <CareRequestsView only="refill" /> : <StaffRefillReviewPanel />, reactions: <StaffAdrReviewPanel />, bills: <CareBillsView />, verification: patientMode ? <CareVerificationView /> : <IdentityQueueView />, team: <CareTeamView />, clinical: <PortalClinicalSharePage /> };
  return <main className="care-workspace" data-testid="customer-workspace">
    {patientMode && <PortalRxTabs active={section} />}
    {!patientMode && <header className="care-heading" data-testid="customer-workspace-header">
      <div className="care-heading-title-row"><h1 className="flex items-center gap-2"><UsersRound size={24} />Clients</h1>
        <span className="care-branch-pill" data-testid="care-branch-context">{branch ? `Branch: ${branchLabel(user, branch)}` : 'Choose a service branch'}</span>
      </div>
    </header>}
    {!patientMode && <nav className="care-nav" aria-label="Customer care navigation">{visibleSections.map(([key, label]) => <NavLink key={key} to={'/customers/' + key} data-testid={`care-nav-${key}`}>{label}</NavLink>)}</nav>}
    {section === 'ai' ? <section className="care-lock-panel" data-testid="care-manual-review-boundary"><h2>Manual pharmacist review</h2><p>Protected records are not sent to hosted AI or OCR. Open Chat or Prescription to share directly with your chosen pharmacist.</p></section> : content[section] || content.records}
  </main>;
};

export default function CustomerWorkspacePage() {
  const { user } = useAuth();
  const location = useLocation();
  const section = location.pathname.split('/')[2] || 'records';
  return <CareProvider user={user} branch={user?.selected_tenant_id || user?.tenant_id}><CareWorkspace section={section} /></CareProvider>;
}

export const PortalCarePage = () => {
  const { href, parsed } = usePortalDestination();
  return <CareWorkspace patientMode section={parsed.page} href={href} />;
};

export const PortalCareShell = ({ children }) => {
  const { patient } = usePortalAuth();
  const { branch, pharmacy } = usePortalDestination();
  return <CareProvider patientMode user={patient} branch={branch?.tenant_id} pharmacy={pharmacy?.tenant_id}>{children}</CareProvider>;
};
